import type {
  BackendErrorResponse,
  BackendSuccessResponse,
} from '@freelancer/freelancer-http';
import { isDefined } from '@freelancer/utils';
import type { ErrorCodeApi } from 'api-typings/errors/errors';
import type {
  DatastoreCollectionType,
  DatastoreDeleteCollectionType,
  DatastoreFetchCollectionType,
  DatastorePushCollectionType,
  DatastoreSetCollectionType,
  DatastoreUpdateCollectionType,
} from './store.model';

type ExtractId<
  C extends DatastoreCollectionType & DatastorePushCollectionType,
> = C['Backend']['Push']['HasIdExtractor'] extends true
  ? DocumentId<C['DocumentType']['id']>
  : C['Backend']['Push']['ReturnType'] extends { readonly id: any }
    ? C['Backend']['Push']['ReturnType']['id']
    : undefined;

type DocumentId<T> = T extends number ? number : string;

export interface BackendPushSuccessResponse<
  C extends DatastoreCollectionType & DatastorePushCollectionType,
> extends BackendSuccessResponse {
  readonly id: ExtractId<C>;
}

export type BackendAllErrorCodes =
  | ErrorCodeApi
  | 'UNKNOWN_ERROR'
  | 'NETWORK_ERROR';

export type BackendPushErrorResponse<
  C extends DatastoreCollectionType & DatastorePushCollectionType,
> = BackendErrorResponse<C['Backend']['Push']['ErrorType']>;
export type BackendSetErrorResponse<
  C extends DatastoreCollectionType & DatastoreSetCollectionType,
> = BackendErrorResponse<C['Backend']['Set']['ErrorType']>;
export type BackendUpdateErrorResponse<
  C extends DatastoreCollectionType & DatastoreUpdateCollectionType,
> = BackendErrorResponse<C['Backend']['Update']['ErrorType']>;
export type BackendDeleteErrorResponse<
  C extends DatastoreCollectionType & DatastoreDeleteCollectionType,
> = BackendErrorResponse<C['Backend']['Delete']['ErrorType']>;

/**
 * Type guard to determine if a value is a {@link BackendErrorResponse} for a specific collection type.
 *
 * @template C - Collection type extending DatastoreCollectionType & DatastorePushCollectionType
 * @param val - Value to type check
 * @returns True if value matches BackendErrorResponse interface structure for the collection
 *
 * @example
 * if (isBackendErrorResponse<MyCollection>(response)) {
 *   handleError(response.errorCode);
 * }
 */
export function isBackendErrorResponse<
  C extends DatastoreCollectionType & DatastorePushCollectionType,
>(val: unknown): val is BackendPushErrorResponse<C> {
  return (
    isDefined(val) &&
    typeof val === 'object' &&
    'status' in val &&
    val.status === 'error' &&
    'errorCode' in val &&
    'requestId' in val
  );
}

export type BackendPushResponse<
  C extends DatastoreCollectionType & DatastorePushCollectionType,
> = BackendPushSuccessResponse<C> | BackendPushErrorResponse<C>;
export type BackendSetResponse<
  C extends DatastoreCollectionType & DatastoreSetCollectionType,
> = BackendSuccessResponse | BackendSetErrorResponse<C>;
export type BackendUpdateResponse<
  C extends DatastoreCollectionType & DatastoreUpdateCollectionType,
> = BackendSuccessResponse | BackendUpdateErrorResponse<C>;
export type BackendDeleteResponse<
  C extends DatastoreCollectionType & DatastoreDeleteCollectionType,
> = BackendSuccessResponse | BackendDeleteErrorResponse<C>;

/** Key-value pairs passed to the backend via query parameters in the URL */
export interface Params {
  readonly [key: string]:
    | string
    | number
    | boolean
    | readonly (number | undefined)[]
    | readonly (string | undefined)[]
    | undefined;
}

interface BackendFetchGetRequest {
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  readonly errorWhitelist?: readonly string[];
}

interface BackendFetchPostRequest<
  C extends DatastoreCollectionType & DatastoreFetchCollectionType,
> {
  readonly payload: C['Backend']['Fetch']['PayloadType'];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
  readonly method: 'POST';
  readonly errorWhitelist?: readonly string[];
}

export type BackendFetchRequest<
  C extends DatastoreCollectionType & DatastoreFetchCollectionType,
> = BackendFetchGetRequest | BackendFetchPostRequest<C>;

export interface BackendPushRequest<
  C extends DatastoreCollectionType & DatastorePushCollectionType,
> {
  readonly payload: C['Backend']['Push']['PayloadType'];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;

  /** A selector function to retrieve the id of the newly created document */
  readonly extractId?: ExtractIdFunction<C>;
}

export type ExtractIdFunction<
  C extends DatastoreCollectionType & DatastorePushCollectionType,
> = (result: C['Backend']['Push']['ReturnType']) => C['DocumentType']['id'];

export interface BackendSetRequest<
  C extends DatastoreCollectionType & DatastoreSetCollectionType,
> {
  readonly payload: C['Backend']['Set']['PayloadType'];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
}

export interface BackendUpdateRequest<
  C extends DatastoreCollectionType & DatastoreUpdateCollectionType,
> {
  readonly payload: C['Backend']['Update']['PayloadType'];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  /**
   * Serialises the body as `application/x-www-form-urlencoded` instead of
   * `application/json` (the default)
   */
  readonly asFormData?: boolean;
  readonly method: 'POST' | 'PUT';
}

export interface BackendDeleteRequest<
  C extends DatastoreCollectionType & DatastoreDeleteCollectionType,
> {
  readonly payload: C['Backend']['Delete']['PayloadType'];
  readonly endpoint: string;
  readonly params?: Params;
  readonly isGaf?: boolean;
  readonly asFormData?: boolean;
  readonly method: 'POST' | 'PUT' | 'DELETE';
}
